import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, settingsAPI } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyLogo, setCompanyLogo] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  // Fetch company logo once authenticated
  useEffect(() => {
    if (user) {
      settingsAPI.getLogo().then(res => setCompanyLogo(res.data.logo || '')).catch(() => {});
    } else {
      setCompanyLogo('');
    }
  }, [user]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.getCurrentUser();
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await authAPI.login({ email: String(email || '').trim(), password });
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (userData) => {
    const response = await authAPI.register({
      ...userData,
      email: String(userData?.email || '').trim(),
    });
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    companyLogo,
    setCompanyLogo,
    // Multi-tenant helpers
    companyId: user?.companyId || null,
    companyName: user?.companyName || user?.company || '',
    userRole: user?.role || 'user',
    isOwner: user?.role === 'owner',
    isAdmin: ['owner', 'admin'].includes(user?.role),
    isMasterAdmin: user?.role === 'masterAdmin',
    // Subscription helpers
    subscriptionStatus: user?.subscription?.status || null,
    accountLocked: !!user?.subscription?.accountLocked,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
